import * as faceapi from 'face-api.js';

let faceModelLoaded = false;
async function loadFaceModel() {
  if (faceModelLoaded) return;
  await faceapi.nets.ssdMobilenetv1.loadFromUri('/models');
  faceModelLoaded = true;
}

/** Detect face, crop to 16×16, equalize, and return 256‑bit hash. */
export async function computeFaceDetectionHash(dataUrl: string): Promise<string | null> {
  await loadFaceModel();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = async () => {
      const maxDim = 800;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      const detections = await faceapi.detectAllFaces(canvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.4, maxResults: 1 }));
      if (detections.length === 0) {
        resolve(null);
        return;
      }

      const box = detections[0].box;
      const faceCanvas = document.createElement('canvas');
      faceCanvas.width = 16;
      faceCanvas.height = 16;
      const faceCtx = faceCanvas.getContext('2d')!;
      faceCtx.drawImage(canvas, box.x, box.y, box.width, box.height, 0, 0, 16, 16);

      const imageData = faceCtx.getImageData(0, 0, 16, 16);
      equalize(imageData.data, 16, 16);   // reuse the existing equalize function
      resolve(perceptualHash(imageData.data));
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/** Apply histogram equalization to an ImageData (in‑place) - overloaded version to match Edge Server */
function equalize(imageDataOrData: ImageData | Uint8ClampedArray, width?: number, height?: number): void {
  let data: Uint8ClampedArray;
  let w: number, h: number;

  if (imageDataOrData instanceof ImageData) {
    data = imageDataOrData.data;
    w = imageDataOrData.width;
    h = imageDataOrData.height;
  } else {
    data = imageDataOrData;
    w = width!;
    h = height!;
  }

  const histogram = new Array(256).fill(0);
  for (let i = 0; i < data.length; i += 4) {
    const l = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    histogram[l]++;
  }
  const total = w * h;
  const lut = new Array(256);
  let sum = 0;
  for (let i = 0; i < 256; i++) {
    sum += histogram[i];
    lut[i] = Math.round((sum / total) * 255);
  }
  for (let i = 0; i < data.length; i += 4) {
    const l = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    const v = lut[l];
    const scale = l > 0 ? v / l : 1;
    data[i] = Math.min(255, data[i] * scale);
    data[i + 1] = Math.min(255, data[i + 1] * scale);
    data[i + 2] = Math.min(255, data[i + 2] * scale);
  }
}

function perceptualHash(data: Uint8ClampedArray): string {
  const gray: number[] = [];
  let total = 0;
  for (let i = 0; i < data.length; i += 4) {
    const g = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
    gray.push(g);
    total += g;
  }
  const avg = total / gray.length;
  let hash = '';
  for (const g of gray) hash += g > avg ? '1' : '0';
  return hash;
}

/** 64‑bit hash (8×8) */
export function computeImageHashFromDataUrl(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // 1 – draw original
      const temp = document.createElement('canvas');
      temp.width = img.width;
      temp.height = img.height;
      const tctx = temp.getContext('2d')!;
      tctx.drawImage(img, 0, 0);

      // 2 – equalize
      const imageData = tctx.getImageData(0, 0, temp.width, temp.height);
      equalize(imageData);
      tctx.putImageData(imageData, 0, 0);

      // 3 – resize equalized image to 8×8
      const final = document.createElement('canvas');
      final.width = 8;
      final.height = 8;
      const fctx = final.getContext('2d')!;
      fctx.drawImage(temp, 0, 0, 8, 8);

      // 4 – compute hash
      const finalData = fctx.getImageData(0, 0, 8, 8);
      resolve(perceptualHash(finalData.data));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

/** 256‑bit face hash (16×16) with lighting normalisation */
export function computeFaceHash(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // 1 – draw original onto an intermediate canvas
      const temp = document.createElement('canvas');
      temp.width = img.width;
      temp.height = img.height;
      const tctx = temp.getContext('2d')!;
      tctx.drawImage(img, 0, 0);

      // 2 – get image data and equalize it
      const imageData = tctx.getImageData(0, 0, temp.width, temp.height);
      equalize(imageData);
      tctx.putImageData(imageData, 0, 0);

      // 3 – resize equalized image to 16×16
      const final = document.createElement('canvas');
      final.width = 16;
      final.height = 16;
      const fctx = final.getContext('2d')!;
      fctx.drawImage(temp, 0, 0, 16, 16);

      // 4 – compute hash on normalised pixels
      const finalData = fctx.getImageData(0, 0, 16, 16);
      resolve(perceptualHash(finalData.data));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}